import { memo, useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  AlertCircle,
  Loader2,
  MessageSquareIcon,
  PenBoxIcon,
  SearchIcon,
  SidebarClose,
  SidebarOpen,
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTutorial } from '@/features/onboarding/useTutorial';

import { useChatActions, useChatHistory } from '../context/ChatContext';
import { ConversationListItem } from './ConversationListItem';
import { selectVisibleConversations } from '../utils/conversationVisibility';
import type { ConversationSummary } from '../types/chat.types';

// One formatter for the whole list, built once.
const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

const formatDate = (iso: string) => {
  try {
    return DATE_FORMATTER.format(new Date(iso));
  } catch {
    return iso;
  }
};

/**
 * Takes no props by design. It reads the two narrow contexts it actually needs
 * — history data and the (stable) actions — so it is not subscribed to `input`
 * or `streamingText` and therefore cannot re-render while the user types or a
 * response streams. That replaces the memo/useCallback scaffolding this used
 * to need: the coupling is gone rather than compensated for.
 */
export const ChatHistorySidebar = memo(() => {
  const {
    conversations,
    activeConversationId,
    isHistoryLoading,
    historyError,
    historyNextCursor,
    isLoadingMoreHistory,
    isSidebarCollapsed,
  } = useChatHistory();
  const {
    toggleSidebar,
    selectConversation,
    startNewConversation,
    renameConversation,
    deleteConversation,
    loadMoreHistory,
  } = useChatActions();
  const { currentStep } = useTutorial();
  const isMobile = useIsMobile();

  const [pendingDelete, setPendingDelete] = useState<ConversationSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // See conversationVisibility.ts: caps the render to the N most recent
  // conversations, and lets search reach anything older than that. Without
  // this the sidebar renders every conversation the API returns — fine for
  // the tens a normal account has, not fine if that number grows a lot.
  const visibleConversations = useMemo(
    () => selectVisibleConversations(conversations, searchQuery, activeConversationId),
    [conversations, searchQuery, activeConversationId],
  );
  const showLoadMore = !searchQuery.trim() && Boolean(historyNextCursor);

  // The tutorial forces the panel open for its first steps.
  const isTutorialForcingOpen = Boolean(currentStep?.requiresSidebar);
  const isOpen = isTutorialForcingOpen || !isSidebarCollapsed;

  // These three are handed to every row, so their identity decides whether the
  // 60 memo()'d rows re-render. They must NOT depend on `isOpen`, or every
  // open/close would invalidate them and re-render the whole list — exactly
  // the work we're trying to avoid. Acting on a row implies the panel is open,
  // so `isOpen` isn't needed to decide whether to close it.
  const closeOnMobile = useCallback(() => {
    if (isMobile) toggleSidebar();
  }, [isMobile, toggleSidebar]);

  const handleSelect = useCallback((conversationId: string) => {
    void selectConversation(conversationId);
    closeOnMobile();
  }, [selectConversation, closeOnMobile]);

  const handleLoadMore = useCallback(() => {
    void loadMoreHistory();
  }, [loadMoreHistory]);

  const handleNewConversation = useCallback(() => {
    startNewConversation();
    closeOnMobile();
  }, [startNewConversation, closeOnMobile]);

  const handleRename = useCallback(async (conversationId: string, name: string) => {
    try {
      await renameConversation(conversationId, name);
    } catch (err) {
      console.error('[ChatHistorySidebar] Error renaming conversation:', err);
      window.alert(err instanceof Error ? err.message : 'Unable to rename the conversation.');
    }
  }, [renameConversation]);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      await deleteConversation(pendingDelete.conversationId);
      setPendingDelete(null);
    } catch (err) {
      console.error('[ChatHistorySidebar] Error deleting conversation:', err);
      window.alert(err instanceof Error ? err.message : 'Unable to delete the conversation.');
    } finally {
      setIsDeleting(false);
    }
  };

  const newChatButton = (
    <button
      type="button"
      onClick={handleNewConversation}
      data-tutorial="new-chat-btn"
      aria-label="New chat"
      className={clsx(
        'flex items-center rounded-lg transition hover:bg-slate-100 hover:text-slate-900',
        isOpen
          ? 'gap-3 px-3 py-2 text-sm font-medium text-slate-600'
          : 'h-10 w-10 justify-center text-slate-600'
      )}
    >
      <PenBoxIcon className="h-4 w-4" />
      {isOpen && <span>New Chat</span>}
    </button>
  );

  // Collapsed (desktop icon rail, ~64px): none of the text content below fits
  // — it used to render anyway and wrap one character per line ("CHATS",
  // dates, even the search hint). Collapsed shows exactly one icon instead:
  // open the panel, same action as the toggle button up top.
  const collapsedHistoryButton = (
    <button
      type="button"
      onClick={toggleSidebar}
      aria-label="Open chat history"
      className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
    >
      <MessageSquareIcon className="h-4 w-4" />
    </button>
  );

  const conversationList = (
    <div className="flex flex-1 flex-col overflow-hidden">
      {!isOpen ? (
        <div className="flex justify-center pt-1">{collapsedHistoryButton}</div>
      ) : (
        <>
          <div className="px-4 pb-2 pt-3 text-sm font-semibold uppercase tracking-wide text-slate-400">
            Chats
          </div>

          {!isHistoryLoading && !historyError && conversations.length > 0 && (
            <div className="relative px-3 pb-2">
              <SearchIcon className="pointer-events-none absolute left-6 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search conversations"
                aria-label="Search conversations"
                className="w-full rounded-md border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-slate-300 focus:outline-none"
              />
            </div>
          )}

          {isHistoryLoading ? (
            <div className="flex flex-1 items-center justify-center gap-2 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading history…</span>
            </div>
          ) : historyError ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <AlertCircle className="h-8 w-8 text-red-500" />
              <p className="text-sm font-medium text-red-600">Failed to load conversations</p>
              <p className="text-xs text-slate-500">{historyError}</p>
            </div>
          ) : conversations.length > 0 ? (
            visibleConversations.length > 0 ? (
              <>
                <ul data-tutorial="chat-history" className="flex flex-1 flex-col gap-1 overflow-y-auto px-2 py-2">
                  {visibleConversations.map((conversation) => (
                    <li key={conversation.conversationId}>
                      <ConversationListItem
                        conversation={conversation}
                        formattedDate={formatDate(conversation.updatedAt)}
                        isActive={activeConversationId === conversation.conversationId}
                        onSelect={handleSelect}
                        onRename={handleRename}
                        onRequestDelete={setPendingDelete}
                      />
                    </li>
                  ))}
                  {showLoadMore && (
                    <li className="px-1">
                      <button
                        type="button"
                        onClick={handleLoadMore}
                        disabled={isLoadingMoreHistory}
                        className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-200 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isLoadingMoreHistory ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            <span>Loading…</span>
                          </>
                        ) : (
                          <span>Load more</span>
                        )}
                      </button>
                    </li>
                  )}
                </ul>
              </>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center px-6 text-center text-slate-500">
                <p className="text-sm font-medium">No conversations match “{searchQuery.trim()}”.</p>
              </div>
            )
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center px-6 text-center text-slate-500">
              <p className="text-sm font-medium">There are no saved conversations yet.</p>
            </div>
          )}

          <div className="border-t border-slate-200 px-4 py-3 text-xs text-slate-400">
            Dr. Phyto <i>Beta</i>
          </div>
        </>
      )}
    </div>
  );

  const deleteDialog = (
    <AlertDialog
      open={pendingDelete !== null}
      onOpenChange={(open) => {
        if (!open && !isDeleting) setPendingDelete(null);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. The conversation history will be permanently removed.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isDeleting}
            onClick={(event) => {
              event.preventDefault(); // keep it open until the request settles
              void handleConfirmDelete();
            }}
          >
            {isDeleting ? 'Deleting…' : 'Delete conversation'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // A single panel for both sizes. The list is rendered unconditionally, so it
  // is built once — when the history data arrives, while the user is looking at
  // the welcome screen — and never again. Opening is then pure CSS: React does
  // no work at all on the click, which is the only way this feels instant.
  //
  // (It used to be a Radix Sheet. Wrong tool: a Sheet mounts its content on
  // open and throws it away on close, so every single open paid the build cost.)
  return (
    <>
      {/* Mobile scrim */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/20 md:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        aria-label="Chat history"
        className={clsx(
          'absolute inset-y-0 left-0 z-30 flex w-72 flex-col overflow-hidden border-r border-slate-200 bg-white',
          // Mobile slides on transform (composited, no layout per frame);
          // desktop is an in-flow rail, so there it animates width instead.
          'transition-transform duration-200 ease-in-out md:static md:h-full md:translate-x-0 md:transition-[width]',
          isOpen ? 'translate-x-0 shadow-lg md:w-72 md:shadow-none' : '-translate-x-full md:w-16'
        )}
      >
        <div className="flex items-center justify-between px-3 py-4">
          {isOpen && (
            <span className="text-sm font-semibold uppercase tracking-wide text-slate-600">Menu</span>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            aria-label={isOpen ? 'Close history' : 'Open history'}
            className={isOpen ? 'justify-end' : 'm-auto'}
          >
            {isOpen ? <SidebarClose className="h-5 w-5" /> : <SidebarOpen className="h-5 w-5" />}
          </Button>
        </div>

        <nav className={clsx('flex flex-col gap-1 px-3 pb-4', !isOpen && 'items-center')}>
          {newChatButton}
        </nav>

        {conversationList}
      </aside>

      {deleteDialog}
    </>
  );
});

ChatHistorySidebar.displayName = 'ChatHistorySidebar';
